import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, FileText, Users, LogOut, Download, Search, Plus, Trash2, Save, ExternalLink, BarChart3, Briefcase, TrendingUp, UploadCloud, Pencil, X, Eye, Phone, MapPin, Clock, Wallet, CalendarDays, BookOpen, ClipboardList, GripVertical, Globe, Newspaper, KeyRound } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { API, api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/site/Header";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import HubManager from "@/components/admin/HubManager";
import ChangePassword from "@/components/admin/ChangePassword";

const STATUSES = ["new", "reviewed", "contacted"];
const badge = { new: "bg-amber-100 text-amber-700", reviewed: "bg-blue-100 text-blue-700", contacted: "bg-emerald-100 text-emerald-700" };

function StatusPill({ value, onChange, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className={`h-8 w-32 border-0 text-xs font-medium capitalize ${badge[value] || "bg-slate-100"}`}><SelectValue /></SelectTrigger>
      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize text-xs">{s}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [hire, setHire] = useState([]);
  const [apps, setApps] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [content, setContent] = useState(null);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  useEffect(() => { if (!loading && !user) navigate("/admin/login"); }, [loading, user, navigate]);
  useEffect(() => {
    if (!user) return;
    api.get("/admin/hire-requests").then((r) => setHire(r.data)).catch(() => {});
    api.get("/admin/applications").then((r) => setApps(r.data)).catch(() => {});
    api.get("/admin/jobs").then((r) => setJobs(r.data)).catch(() => {});
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, [user]);

  const token = localStorage.getItem("iws_token");
  const setHireStatus = async (id, status) => { setHire(hire.map((h) => h.id === id ? { ...h, status } : h)); await api.patch(`/admin/hire-requests/${id}`, { status }); };
  const setAppStatus = async (id, status) => { setApps(apps.map((a) => a.id === id ? { ...a, status } : a)); await api.patch(`/admin/applications/${id}`, { status }); };
  const exportCsv = (kind) => window.open(`${API}/admin/export/${kind}?auth=${token}`, "_blank");
  const downloadResume = (id) => window.open(`${API}/admin/resume/${id}?auth=${token}`, "_blank");

  const fHire = useMemo(() => hire.filter((h) => JSON.stringify(h).toLowerCase().includes(q.toLowerCase())), [hire, q]);
  const fApps = useMemo(() => apps.filter((a) => JSON.stringify(a).toLowerCase().includes(q.toLowerCase())), [apps, q]);

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center text-navy">Loading…</div>;

  const menu = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "hire", label: "Hire Requests", icon: FileText, count: hire.length },
    { id: "apps", label: "Applications", icon: Users, count: apps.length },
    { id: "jobs", label: "Jobs", icon: Briefcase, count: jobs.length },
    { id: "content", label: "Content Manager", icon: LayoutDashboard },
    { id: "forms", label: "Form Builder", icon: ClipboardList },
    { id: "hub", label: "Knowledge Hub", icon: Newspaper },
    { id: "seo", label: "SEO & Analytics", icon: Globe },
    { id: "legal", label: "Legal Pages", icon: BookOpen },
    { id: "account", label: "Account", icon: KeyRound },
  ];

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="w-64 bg-navy text-slate-300 flex flex-col shrink-0 sticky top-0 h-screen">
        <div className="p-6 border-b border-white/10"><Logo light /></div>
        <nav className="flex-1 p-4 space-y-1">
          {menu.map((m) => (
            <button key={m.id} onClick={() => setTab(m.id)} data-testid={`nav-${m.id}`}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${tab === m.id ? "bg-emerald-brand text-white" : "hover:bg-white/10"}`}>
              <m.icon size={18} /> {m.label}
              {m.count !== undefined && <span className="ml-auto text-xs bg-white/15 rounded-full px-2 py-0.5">{m.count}</span>}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <a href="/" target="_blank" rel="noreferrer" className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm hover:bg-white/10 transition-colors mb-1"><ExternalLink size={18} /> View Site</a>
          <button onClick={async () => { await logout(); navigate("/admin/login"); }} data-testid="logout-btn" className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm hover:bg-white/10 transition-colors"><LogOut size={18} /> Logout</button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-8 sticky top-0 z-10">
          <h1 className="font-display font-semibold text-navy text-lg">{menu.find((m) => m.id === tab)?.label}</h1>
          <div className="text-sm text-slate-500">{user.email}</div>
        </header>

        <div className="p-8">
          {tab === "overview" && <Overview stats={stats} />}
          {tab === "jobs" && <JobsManager jobs={jobs} setJobs={setJobs} />}
          {(tab === "hire" || tab === "apps") && (
            <>
              <div className="flex flex-wrap items-center gap-3 mb-5">
                <div className="relative flex-1 min-w-[220px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <Input data-testid="search-input" placeholder="Search submissions…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 focus-visible:ring-emerald-brand" />
                </div>
                <Button onClick={() => exportCsv(tab === "hire" ? "hire-requests" : "applications")} data-testid="export-csv-btn" variant="outline" className="border-navy text-navy hover:bg-navy hover:text-white gap-2"><Download size={16} /> Export CSV</Button>
              </div>

              {tab === "hire" && (
                <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="hire-table">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500 text-left"><tr>
                      <th className="px-4 py-3 font-medium">Company</th><th className="px-4 py-3 font-medium">Contact</th>
                      <th className="px-4 py-3 font-medium">Email</th><th className="px-4 py-3 font-medium">Phone</th>
                      <th className="px-4 py-3 font-medium">Requirement</th><th className="px-4 py-3 font-medium text-right">Pos.</th>
                      <th className="px-4 py-3 font-medium">Status</th></tr></thead>
                    <tbody>
                      {fHire.map((h) => (
                        <tr key={h.id} className="border-t border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 font-medium text-navy">{h.company_name}</td>
                          <td className="px-4 py-3">{h.contact_person}</td>
                          <td className="px-4 py-3 text-slate-600">{h.email}</td>
                          <td className="px-4 py-3 text-slate-600">{h.phone}</td>
                          <td className="px-4 py-3 text-slate-600">{h.hiring_requirement}</td>
                          <td className="px-4 py-3 text-right">{h.positions}</td>
                          <td className="px-4 py-3"><StatusPill value={h.status} onChange={(v) => setHireStatus(h.id, v)} testid={`hire-status-${h.id}`} /></td>
                        </tr>
                      ))}
                      {fHire.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No hire requests yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}

              {tab === "apps" && (
                <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="apps-table">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500 text-left"><tr>
                      <th className="px-4 py-3 font-medium">Name</th><th className="px-4 py-3 font-medium">Email</th>
                      <th className="px-4 py-3 font-medium">Applied For</th><th className="px-4 py-3 font-medium">Experience</th>
                      <th className="px-4 py-3 font-medium">Exp. CTC</th><th className="px-4 py-3 font-medium">Resume</th>
                      <th className="px-4 py-3 font-medium">Status</th></tr></thead>
                    <tbody>
                      {fApps.map((a) => (
                        <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 font-medium text-navy"><button onClick={() => setDetail(a)} data-testid={`app-view-${a.id}`} className="hover:text-emerald-brand transition-colors">{a.full_name}</button></td>
                          <td className="px-4 py-3 text-slate-600">{a.email}</td>
                          <td className="px-4 py-3 text-slate-600">{a.job_title ? <span className="inline-block bg-emerald-brand/10 text-emerald-brand rounded-full px-2 py-0.5 text-xs">{a.job_title}</span> : "General"}</td>
                          <td className="px-4 py-3">{a.experience}</td>
                          <td className="px-4 py-3">{a.expected_ctc || "—"}</td>
                          <td className="px-4 py-3 flex items-center gap-3">
                            <button onClick={() => setDetail(a)} data-testid={`app-detail-${a.id}`} className="inline-flex items-center gap-1 text-navy hover:text-emerald-brand font-medium"><Eye size={14} /> View</button>
                            <button onClick={() => downloadResume(a.id)} data-testid={`resume-dl-${a.id}`} className="inline-flex items-center gap-1 text-emerald-brand hover:text-emerald-dark font-medium"><Download size={14} /> Resume</button>
                          </td>
                          <td className="px-4 py-3"><StatusPill value={a.status} onChange={(v) => setAppStatus(a.id, v)} testid={`app-status-${a.id}`} /></td>
                        </tr>
                      ))}
                      {fApps.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No applications yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {tab === "content" && content && <ContentManager content={content} setContent={setContent} />}
          {tab === "forms" && content && <FormBuilder content={content} setContent={setContent} />}
          {tab === "hub" && <HubManager />}
          {tab === "seo" && content && <SeoAnalytics content={content} setContent={setContent} />}
          {tab === "legal" && content && <LegalManager content={content} setContent={setContent} />}
          {tab === "account" && <ChangePassword />}
        </div>
      </main>
      {detail && <AppDetailDialog app={detail} token={token} onClose={() => setDetail(null)} onStatus={(v) => { setAppStatus(detail.id, v); setDetail({ ...detail, status: v }); }} />}
    </div>
  );
}

function ContentManager({ content, setContent }) {
  const [c, setC] = useState(content);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setC({ ...c, [k]: v });
  const setContact = (k, v) => setC({ ...c, contact: { ...c.contact, [k]: v } });
  const setForm = (which, k, v) => setC({ ...c, forms: { ...(c.forms || {}), [which]: { ...((c.forms || {})[which] || {}), [k]: v } } });

  const save = async () => {
    setSaving(true);
    try { await api.put("/content", c); setContent(c); toast.success("Content saved & published!"); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const upd = (list, i, k, v) => c[list].map((it, idx) => idx === i ? { ...it, [k]: v } : it);

  return (
    <div className="space-y-8 max-w-4xl">
      <div className="flex justify-end sticky top-20 z-10">
        <Button onClick={save} disabled={saving} data-testid="save-content-btn" className="bg-emerald-brand hover:bg-emerald-dark gap-2"><Save size={16} /> {saving ? "Saving…" : "Save & Publish"}</Button>
      </div>

      <LogoUpload />

      <Card title="Hero Section">
        <div><Label>Heading</Label><Textarea data-testid="content-hero-heading" className="mt-1.5" value={c.hero_heading} onChange={(e) => set("hero_heading", e.target.value)} /></div>
        <div><Label>Sub-heading</Label><Textarea data-testid="content-hero-sub" className="mt-1.5" value={c.hero_subheading} onChange={(e) => set("hero_subheading", e.target.value)} /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Primary CTA</Label><Input className="mt-1.5" value={c.hero_cta_primary} onChange={(e) => set("hero_cta_primary", e.target.value)} /></div>
          <div><Label>Secondary CTA</Label><Input className="mt-1.5" value={c.hero_cta_secondary} onChange={(e) => set("hero_cta_secondary", e.target.value)} /></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Badge title</Label><Input data-testid="content-badge-title" className="mt-1.5" value={c.hero_badge_title || ""} onChange={(e) => set("hero_badge_title", e.target.value)} /></div>
          <div><Label>Badge subtitle</Label><Input data-testid="content-badge-sub" className="mt-1.5" value={c.hero_badge_subtitle || ""} onChange={(e) => set("hero_badge_subtitle", e.target.value)} /></div>
        </div>
        <MediaUpload label="Hero image" value={c.hero_image} onChange={(id) => set("hero_image", id)} hint="Large image shown beside the hero heading (recommended 1200×840)." />
      </Card>

      <Card title="About Section">
        <div><Label>Title</Label><Input data-testid="content-about-title" className="mt-1.5" value={c.about_title} onChange={(e) => set("about_title", e.target.value)} /></div>
        <div><Label>Text</Label><Textarea className="mt-1.5" rows={4} value={c.about_text} onChange={(e) => set("about_text", e.target.value)} /></div>
        <MediaUpload label="About image" value={c.about_image} onChange={(id) => set("about_image", id)} hint="Image shown in the About section (recommended 1000×800)." />
      </Card>

      <ListEditor title="Services" list={c.services} onAdd={() => set("services", [...c.services, { icon: "briefcase", title: "New Service", description: "" }])}
        onRemove={(i) => set("services", c.services.filter((_, idx) => idx !== i))}
        render={(s, i) => (<>
          <Input placeholder="Title" value={s.title} onChange={(e) => set("services", upd("services", i, "title", e.target.value))} />
          <Input placeholder="Description" value={s.description} onChange={(e) => set("services", upd("services", i, "description", e.target.value))} />
        </>)} />

      <ListEditor title="Why Choose IWS" list={c.why_choose} onAdd={() => set("why_choose", [...c.why_choose, { icon: "check-circle", title: "New Point", description: "" }])}
        onRemove={(i) => set("why_choose", c.why_choose.filter((_, idx) => idx !== i))}
        render={(w, i) => (<>
          <Input placeholder="Title" value={w.title} onChange={(e) => set("why_choose", upd("why_choose", i, "title", e.target.value))} />
          <Input placeholder="Description" value={w.description} onChange={(e) => set("why_choose", upd("why_choose", i, "description", e.target.value))} />
        </>)} />

      <ListEditor title="Recruitment Process" list={c.process} onAdd={() => set("process", [...c.process, { title: "New Step", description: "" }])}
        onRemove={(i) => set("process", c.process.filter((_, idx) => idx !== i))}
        render={(p, i) => (<>
          <Input placeholder="Step title" value={p.title} onChange={(e) => set("process", upd("process", i, "title", e.target.value))} />
          <Input placeholder="Step description" value={p.description} onChange={(e) => set("process", upd("process", i, "description", e.target.value))} />
        </>)} />

      <Card title="Industries">
        <div className="flex flex-wrap gap-2">
          {c.industries.map((ind, i) => (
            <span key={i} className="inline-flex items-center gap-1 bg-slate-100 rounded-full pl-3 pr-1 py-1 text-sm">
              <input className="bg-transparent outline-none w-auto" style={{ width: `${Math.max(ind.length, 4)}ch` }} value={ind} onChange={(e) => set("industries", c.industries.map((v, idx) => idx === i ? e.target.value : v))} />
              <button onClick={() => set("industries", c.industries.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-500"><Trash2 size={13} /></button>
            </span>
          ))}
          <button onClick={() => set("industries", [...c.industries, "New Industry"])} data-testid="add-industry" className="inline-flex items-center gap-1 text-emerald-brand text-sm font-medium"><Plus size={14} /> Add</button>
        </div>
      </Card>

      <Card title="Contact Info">
        <div><Label>Phones (comma separated)</Label><Input className="mt-1.5" value={(c.contact.phones || []).join(", ")} onChange={(e) => setContact("phones", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Email</Label><Input className="mt-1.5" value={c.contact.email} onChange={(e) => setContact("email", e.target.value)} /></div>
          <div><Label>LinkedIn URL</Label><Input className="mt-1.5" value={c.contact.linkedin} onChange={(e) => setContact("linkedin", e.target.value)} /></div>
        </div>
        <div><Label>Address</Label><Input className="mt-1.5" value={c.contact.address} onChange={(e) => setContact("address", e.target.value)} /></div>
        <div><Label>Form notification email <span className="text-slate-400 font-normal">(where Hire Talent &amp; Resume submissions are emailed)</span></Label><Input data-testid="content-notify-email" type="email" className="mt-1.5" value={c.notification_email || ""} onChange={(e) => set("notification_email", e.target.value)} /></div>
      </Card>

      <Card title="Header & Footer">
        <p className="text-sm text-slate-500 -mt-2">The top bar phone, email and LinkedIn come from the <b>Contact Info</b> card above.</p>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Header "Hire" button label</Label><Input data-testid="content-hire-label" className="mt-1.5" value={c.hire_cta_label || ""} onChange={(e) => set("hire_cta_label", e.target.value)} /></div>
          <div><Label>Header "Resume" button label</Label><Input data-testid="content-resume-label" className="mt-1.5" value={c.resume_cta_label || ""} onChange={(e) => set("resume_cta_label", e.target.value)} /></div>
        </div>
        <div><Label>Footer tagline</Label><Textarea data-testid="content-footer-tagline" className="mt-1.5" rows={2} value={c.footer_tagline || ""} onChange={(e) => set("footer_tagline", e.target.value)} /></div>
        <div><Label>Footer copyright</Label><Input data-testid="content-copyright" className="mt-1.5" value={c.footer_copyright || ""} onChange={(e) => set("footer_copyright", e.target.value)} placeholder="© {year} Company. All rights reserved." /><p className="text-xs text-slate-400 mt-1">Type {"{year}"} to auto-insert the current year.</p></div>
        <div>
          <div className="flex items-center justify-between mb-2"><Label>Footer social icons</Label>
            <Button size="sm" variant="outline" onClick={() => set("footer_socials", [...(c.footer_socials || []), { id: Date.now().toString(), platform: "LinkedIn", url: "", icon: "linkedin" }])} className="gap-1 border-emerald-brand text-emerald-brand hover:bg-emerald-brand hover:text-white" data-testid="add-social"><Plus size={13} /> Add</Button></div>
          <div className="space-y-2">
            {(c.footer_socials || []).map((s, i) => (
              <div key={i} className="grid grid-cols-[130px_1fr_auto] gap-2 items-center">
                <select className="h-9 rounded-md border border-slate-200 text-sm px-2 bg-white" value={s.icon} onChange={(e) => set("footer_socials", (c.footer_socials || []).map((x, idx) => idx === i ? { ...x, icon: e.target.value, platform: e.target.value } : x))}>
                  {["linkedin", "twitter", "facebook", "instagram", "youtube", "github", "globe"].map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
                <Input placeholder="https://…" value={s.url} onChange={(e) => set("footer_socials", (c.footer_socials || []).map((x, idx) => idx === i ? { ...x, url: e.target.value } : x))} />
                <button onClick={() => set("footer_socials", (c.footer_socials || []).filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-500"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <FormEditor title="Hire Talent Form" which="hire" form={(c.forms || {}).hire || {}} setForm={setForm} testPrefix="hire" />
      <FormEditor title="Submit Resume Form" which="resume" form={(c.forms || {}).resume || {}} setForm={setForm} testPrefix="resume" />
    </div>
  );
}

function FormEditor({ title, which, form, setForm, testPrefix }) {
  return (
    <Card title={title}>
      <p className="text-sm text-slate-500 -mt-2">Edit the page heading, button and the thank-you message shown after submission.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><Label>Page title</Label><Input data-testid={`form-${testPrefix}-title`} className="mt-1.5" value={form.title || ""} onChange={(e) => setForm(which, "title", e.target.value)} /></div>
        <div><Label>Submit button label</Label><Input data-testid={`form-${testPrefix}-submit`} className="mt-1.5" value={form.submit_label || ""} onChange={(e) => setForm(which, "submit_label", e.target.value)} /></div>
      </div>
      <div><Label>Page subtitle</Label><Textarea data-testid={`form-${testPrefix}-subtitle`} className="mt-1.5" rows={2} value={form.subtitle || ""} onChange={(e) => setForm(which, "subtitle", e.target.value)} /></div>
      <div className="pt-2 border-t border-slate-100">
        <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">Thank-you message (after submit)</p>
        <div><Label>Success title</Label><Input data-testid={`form-${testPrefix}-success-title`} className="mt-1.5" value={form.success_title || ""} onChange={(e) => setForm(which, "success_title", e.target.value)} /></div>
        <div className="mt-3"><Label>Success message</Label><Textarea data-testid={`form-${testPrefix}-success-message`} className="mt-1.5" rows={3} value={form.success_message || ""} onChange={(e) => setForm(which, "success_message", e.target.value)} /></div>
      </div>
    </Card>
  );
}

const Card = ({ title, children }) => (  <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
    <h3 className="font-display font-semibold text-navy">{title}</h3>{children}
  </div>
);

const ListEditor = ({ title, list, render, onAdd, onRemove }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-6">
    <div className="flex items-center justify-between mb-4"><h3 className="font-display font-semibold text-navy">{title}</h3>
      <Button size="sm" variant="outline" onClick={onAdd} className="gap-1 border-emerald-brand text-emerald-brand hover:bg-emerald-brand hover:text-white"><Plus size={14} /> Add</Button></div>
    <div className="space-y-3">
      {list.map((it, i) => (
        <div key={i} className="flex items-center gap-2 grid grid-cols-[1fr_2fr_auto]">
          {render(it, i)}
          <button onClick={() => onRemove(i)} className="text-slate-400 hover:text-red-500"><Trash2 size={16} /></button>
        </div>
      ))}
    </div>
  </div>
);


function Row({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0">
      <Icon size={16} className="text-emerald-brand mt-0.5 shrink-0" />
      <div className="min-w-0"><div className="text-xs uppercase tracking-wide text-slate-400">{label}</div><div className="text-sm text-navy break-words">{value}</div></div>
    </div>
  );
}

function AppDetailDialog({ app, token, onClose, onStatus }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loadErr, setLoadErr] = useState(false);
  const [notes, setNotes] = useState(app.notes || "");
  const [savingNotes, setSavingNotes] = useState(false);
  const isPdf = (app.resume_filename || "").toLowerCase().endsWith(".pdf");

  useEffect(() => {
    let url;
    if (!isPdf) return;
    api.get(`/admin/resume/${app.id}`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setBlobUrl(url); })
      .catch(() => setLoadErr(true));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [app.id, isPdf]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try { await api.patch(`/admin/applications/${app.id}/notes`, { notes }); app.notes = notes; toast.success("Notes saved"); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail) || "Failed to save notes"); }
    finally { setSavingNotes(false); }
  };

  const download = () => window.open(`${API}/admin/resume/${app.id}?auth=${token}`, "_blank");

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="app-detail-dialog" className="max-w-4xl max-h-[90vh] overflow-hidden p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b border-slate-200">
          <DialogTitle className="font-display text-navy flex items-center gap-3">
            {app.full_name}
            {app.job_title ? <span className="text-xs font-medium bg-emerald-brand/10 text-emerald-brand rounded-full px-2 py-0.5">{app.job_title}</span> : <span className="text-xs font-medium bg-slate-100 text-slate-500 rounded-full px-2 py-0.5">General</span>}
          </DialogTitle>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-0 overflow-y-auto max-h-[calc(90vh-64px)]">
          <div className="p-6 space-y-1 border-r border-slate-100">
            <Row icon={FileText} label="Email" value={app.email} />
            <Row icon={Phone} label="Phone" value={app.phone} />
            <Row icon={Clock} label="Experience" value={app.experience} />
            <Row icon={MapPin} label="Current Location" value={app.current_location} />
            <Row icon={MapPin} label="Preferred Location" value={app.preferred_location} />
            <Row icon={Wallet} label="Current CTC" value={app.current_ctc} />
            <Row icon={Wallet} label="Expected CTC" value={app.expected_ctc} />
            <Row icon={CalendarDays} label="Notice Period" value={app.notice_period} />
            <Row icon={CalendarDays} label="Applied On" value={app.created_at ? new Date(app.created_at).toLocaleString() : ""} />
            <div className="flex items-center gap-3 pt-4">
              <span className="text-xs uppercase tracking-wide text-slate-400">Status</span>
              <StatusPill value={app.status} onChange={onStatus} testid={`detail-status-${app.id}`} />
            </div>
            <Button onClick={download} data-testid="detail-download" className="mt-4 w-full bg-navy hover:bg-navy-900 gap-2"><Download size={16} /> Download Resume</Button>
            <div className="mt-5 pt-4 border-t border-slate-100">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-400 mb-2"><Pencil size={13} /> Private Notes</div>
              <Textarea data-testid="detail-notes" rows={4} placeholder="Add follow-up notes (visible only to admins)…" value={notes} onChange={(e) => setNotes(e.target.value)} className="focus-visible:ring-emerald-brand" />
              <Button onClick={saveNotes} disabled={savingNotes} data-testid="detail-notes-save" size="sm" className="mt-2 bg-emerald-brand hover:bg-emerald-dark gap-2"><Save size={14} /> {savingNotes ? "Saving…" : "Save Notes"}</Button>
            </div>
          </div>
          <div className="p-6 bg-slate-50">
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Resume Preview</div>
            {isPdf ? (
              blobUrl ? <iframe title="resume" src={blobUrl} data-testid="resume-preview" className="w-full h-[520px] rounded-lg border border-slate-200 bg-white" />
              : loadErr ? <p className="text-sm text-slate-500">Couldn't load preview. Use Download instead.</p>
              : <p className="text-sm text-slate-500">Loading preview…</p>
            ) : (
              <div className="h-[520px] flex flex-col items-center justify-center text-center gap-3 rounded-lg border border-dashed border-slate-300 bg-white">
                <FileText size={40} className="text-slate-300" />
                <p className="text-sm text-slate-500 px-6">Inline preview is available for PDF resumes only.<br />This file is <b>{app.resume_filename}</b>.</p>
                <Button onClick={download} variant="outline" className="gap-2 border-navy text-navy"><Download size={15} /> Download to view</Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function Kpi({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-4">
      <span className={`h-12 w-12 rounded-xl flex items-center justify-center ${accent}`}><Icon size={22} /></span>
      <div><div className="text-2xl font-display font-bold text-navy">{value}</div><div className="text-xs text-slate-500">{label}</div></div>
    </div>
  );
}

function Overview({ stats }) {
  if (!stats) return <p className="text-slate-500">Loading stats…</p>;
  return (
    <div className="space-y-8" data-testid="overview-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Hire Requests" value={stats.hire_total} icon={FileText} accent="bg-navy/5 text-navy" />
        <Kpi label="Applications" value={stats.apps_total} icon={Users} accent="bg-emerald-brand/10 text-emerald-brand" />
        <Kpi label="Open Jobs" value={stats.open_jobs} icon={Briefcase} accent="bg-navy/5 text-navy" />
        <Kpi label="New Leads" value={stats.hire_new + stats.apps_new} icon={TrendingUp} accent="bg-emerald-brand/10 text-emerald-brand" />
      </div>
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-display font-semibold text-navy mb-1">Leads this week</h3>
        <p className="text-sm text-slate-500 mb-6">Hire requests and candidate applications over the last 7 days</p>
        <div style={{ width: "100%", height: 300 }}>
          <ResponsiveContainer width="100%" height="100%" minHeight={300}>
            <BarChart data={stats.weekly} barGap={6}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
              <XAxis dataKey="date" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#64748b" }} />
              <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#64748b" }} />
              <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
              <Bar dataKey="hire" name="Hire Requests" fill="#0A2540" radius={[6, 6, 0, 0]} />
              <Bar dataKey="applications" name="Applications" fill="#2E8B57" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="flex gap-6 mt-4 text-sm text-slate-600">
          <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-sm bg-navy" /> Hire Requests</span>
          <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-sm bg-emerald-brand" /> Applications</span>
        </div>
      </div>
    </div>
  );
}

const emptyJob = { title: "", department: "", location: "", job_type: "Full-time", experience: "", description: "", is_open: true };

function JobsManager({ jobs, setJobs }) {
  const [editing, setEditing] = useState(null); // null | job object (with optional id)
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!editing.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      if (editing.id) {
        const { id, created_at, ...body } = editing;
        await api.put(`/admin/jobs/${id}`, body);
        setJobs(jobs.map((j) => j.id === id ? { ...j, ...body } : j));
      } else {
        const { data } = await api.post("/admin/jobs", editing);
        setJobs([data, ...jobs]);
      }
      toast.success("Job saved");
      setEditing(null);
    } catch (err) { toast.error(apiErr(err.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    setJobs(jobs.filter((j) => j.id !== id));
    try { await api.delete(`/admin/jobs/${id}`); toast.success("Job deleted"); } catch (_) {}
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="jobs-panel">
      <div className="flex justify-end">
        <Button onClick={() => setEditing({ ...emptyJob })} data-testid="add-job-btn" className="bg-emerald-brand hover:bg-emerald-dark gap-2"><Plus size={16} /> New Job</Button>
      </div>

      {editing && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4" data-testid="job-form">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-semibold text-navy">{editing.id ? "Edit Job" : "New Job"}</h3>
            <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-navy"><X size={18} /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Title *</Label><Input data-testid="job-title" className="mt-1.5" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
            <div><Label>Department</Label><Input className="mt-1.5" value={editing.department} onChange={(e) => setEditing({ ...editing, department: e.target.value })} /></div>
            <div><Label>Location</Label><Input className="mt-1.5" value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} /></div>
            <div><Label>Experience</Label><Input className="mt-1.5" placeholder="e.g. 3-5 years" value={editing.experience} onChange={(e) => setEditing({ ...editing, experience: e.target.value })} /></div>
            <div>
              <Label>Job Type</Label>
              <Select value={editing.job_type} onValueChange={(v) => setEditing({ ...editing, job_type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="job-type"><SelectValue /></SelectTrigger>
                <SelectContent>{["Full-time", "Part-time", "Contract", "Internship", "Remote"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pt-7">
              <input type="checkbox" id="isopen" checked={editing.is_open} onChange={(e) => setEditing({ ...editing, is_open: e.target.checked })} className="h-4 w-4 accent-emerald-600" data-testid="job-open" />
              <Label htmlFor="isopen" className="cursor-pointer">Open (visible on public site)</Label>
            </div>
          </div>
          <div><Label>Description</Label><Textarea className="mt-1.5" rows={5} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
          <div className="flex gap-3"><Button onClick={save} disabled={saving} data-testid="job-save" className="bg-emerald-brand hover:bg-emerald-dark gap-2"><Save size={16} /> {saving ? "Saving…" : "Save Job"}</Button>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button></div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="jobs-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left"><tr>
            <th className="px-4 py-3 font-medium">Title</th><th className="px-4 py-3 font-medium">Department</th>
            <th className="px-4 py-3 font-medium">Location</th><th className="px-4 py-3 font-medium">Type</th>
            <th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3 font-medium text-right">Actions</th></tr></thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-navy">{j.title}</td>
                <td className="px-4 py-3 text-slate-600">{j.department || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{j.location || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{j.job_type}</td>
                <td className="px-4 py-3"><span className={`text-xs font-medium rounded-full px-2 py-0.5 ${j.is_open ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{j.is_open ? "Open" : "Closed"}</span></td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing({ ...j })} data-testid={`job-edit-${j.id}`} className="text-navy hover:text-emerald-brand mr-3"><Pencil size={16} /></button>
                  <button onClick={() => remove(j.id)} data-testid={`job-delete-${j.id}`} className="text-slate-400 hover:text-red-500"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
            {jobs.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No jobs posted yet. Click "New Job" to add one.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LogoUpload() {
  const [uploading, setUploading] = useState(false);
  const [v, setV] = useState(localStorage.getItem("iws_logo_v") || "");
  const [hasLogo, setHasLogo] = useState(true);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const ver = String(data.version || Date.now());
      localStorage.setItem("iws_logo_v", ver);
      setV(ver); setHasLogo(true);
      toast.success("Logo updated! Refresh public pages to see it everywhere.");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  const remove = async () => {
    try {
      await api.delete("/admin/logo");
      localStorage.removeItem("iws_logo_v");
      setHasLogo(false);
      toast.success("Logo removed — the text logo will show again.");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail) || "Remove failed"); }
  };

  return (
    <Card title="Brand Logo">
      <p className="text-sm text-slate-500 -mt-2">Upload your IWS logo (PNG, JPG, WEBP or SVG, max 2MB). It replaces the text mark in the header & footer.</p>
      <div className="flex flex-wrap items-center gap-5">
        <div className="h-16 w-40 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
          {hasLogo ? <img src={`${API}/logo${v ? `?v=${v}` : ""}`} alt="Current logo" className="max-h-14 max-w-full object-contain" onError={() => setHasLogo(false)} /> : <span className="text-xs text-slate-400">Text logo active</span>}
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer bg-navy text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-navy-900 transition-colors" data-testid="logo-upload-label">
          <UploadCloud size={16} /> {uploading ? "Uploading…" : "Upload Logo"}
          <input type="file" accept=".png,.jpg,.jpeg,.webp,.svg" className="hidden" data-testid="logo-upload-input" onChange={upload} />
        </label>
        {hasLogo && <Button variant="outline" onClick={remove} data-testid="logo-remove-btn" className="gap-2 text-red-600 border-red-200 hover:bg-red-50"><Trash2 size={15} /> Remove</Button>}
      </div>
    </Card>
  );
}


function MediaUpload({ label, value, onChange, hint }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.id);
      toast.success(`${label} uploaded — click Save & Publish to go live`);
    } catch (err) { toast.error(apiErr(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };
  return (
    <div>
      <Label>{label}</Label>
      {hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}
      <div className="mt-2 flex items-center gap-4">
        <div className="h-20 w-32 rounded-lg border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center shrink-0">
          {value ? <img src={`${API}/media/${value}`} alt="" className="h-full w-full object-cover" /> : <span className="text-xs text-slate-400 px-2 text-center">Using default</span>}
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer bg-navy text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-navy-900 transition-colors" data-testid={`media-label-${label.toLowerCase().replace(/\s/g, "-")}`}>
          <UploadCloud size={15} /> {uploading ? "Uploading…" : "Upload"}
          <input type="file" accept="image/*" className="hidden" data-testid={`media-input-${label.toLowerCase().replace(/\s/g, "-")}`} onChange={upload} />
        </label>
        {value && <Button type="button" variant="outline" size="sm" onClick={() => onChange("")} className="gap-1"><Trash2 size={14} /> Reset</Button>}
      </div>
    </div>
  );
}

function LegalManager({ content, setContent }) {
  const [c, setC] = useState(content);
  const [saving, setSaving] = useState(false);
  const setDoc = (which, k, v) => setC({ ...c, [which]: { ...(c[which] || {}), [k]: v } });
  const save = async () => {
    setSaving(true);
    try { await api.put("/content", c); setContent(c); toast.success("Legal pages saved & published"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };
  const docs = [["legal_privacy", "Privacy Policy", "/privacy"], ["legal_terms", "Terms & Conditions", "/terms"]];
  return (
    <div className="space-y-8 max-w-4xl" data-testid="legal-panel">
      <div className="flex justify-end sticky top-20 z-10">
        <Button onClick={save} disabled={saving} data-testid="save-legal-btn" className="bg-emerald-brand hover:bg-emerald-dark gap-2"><Save size={16} /> {saving ? "Saving…" : "Save & Publish"}</Button>
      </div>
      {docs.map(([key, label, path]) => {
        const d = c[key] || {};
        return (
          <Card key={key} title={label}>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="h-4 w-4 accent-emerald-600" checked={d.enabled !== false} data-testid={`legal-${key}-enabled`} onChange={(e) => setDoc(key, "enabled", e.target.checked)} />
              <span className="text-sm text-navy">Show this page &amp; footer link (visitors can open <b>{path}</b>)</span>
            </label>
            <div><Label>Page title</Label><Input data-testid={`legal-${key}-title`} className="mt-1.5" value={d.title || ""} onChange={(e) => setDoc(key, "title", e.target.value)} /></div>
            <div><Label>Content</Label><Textarea data-testid={`legal-${key}-body`} className="mt-1.5" rows={12} value={d.body || ""} onChange={(e) => setDoc(key, "body", e.target.value)} /></div>
            <div className="max-w-xs"><Label>Open as</Label>
              <select className="mt-1.5 h-9 w-full rounded-md border border-slate-200 text-sm px-2 bg-white" data-testid={`legal-${key}-mode`} value={d.mode || "page"} onChange={(e) => setDoc(key, "mode", e.target.value)}>
                <option value="page">Separate page ({path})</option>
                <option value="popup">Popup on same page</option>
              </select>
            </div>
          </Card>
        );
      })}
    </div>
  );
}



const FIELD_TYPES = ["text", "email", "tel", "number", "textarea", "select", "file"];

function FormBuilder({ content, setContent }) {
  const [which, setWhich] = useState("hire");
  const [fb, setFb] = useState(content.form_builder || { hire: [], resume: [] });
  const [saving, setSaving] = useState(false);
  const dragIdx = useRef(null);
  const list = fb[which] || [];
  const setList = (nl) => setFb({ ...fb, [which]: nl });
  const upd = (i, k, v) => setList(list.map((f, idx) => idx === i ? { ...f, [k]: v } : f));
  const add = () => { const t = Date.now(); setList([...list, { id: "f" + t, label: "New Field", name: "field_" + t, type: "text", required: false, placeholder: "", maxlength: 0, options: [], width: "half", enabled: true }]); };
  const del = (i) => setList(list.filter((_, idx) => idx !== i));
  const onDrop = (i) => { const from = dragIdx.current; if (from === null || from === i) return; const arr = [...list]; const [m] = arr.splice(from, 1); arr.splice(i, 0, m); setList(arr); dragIdx.current = null; };
  const save = async () => {
    setSaving(true);
    try { const merged = { ...content, form_builder: fb }; await api.put("/content", merged); setContent(merged); toast.success("Form saved & published"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="form-builder-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-lg bg-slate-100 p-1">
          {[["hire", "Hire Talent"], ["resume", "Submit Resume"]].map(([k, lbl]) => (
            <button key={k} onClick={() => setWhich(k)} data-testid={`fb-tab-${k}`} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${which === k ? "bg-white text-navy shadow-sm" : "text-slate-500"}`}>{lbl}</button>
          ))}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={add} data-testid="fb-add-field" className="gap-1 border-emerald-brand text-emerald-brand hover:bg-emerald-brand hover:text-white"><Plus size={14} /> Add Field</Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="fb-save" className="bg-emerald-brand hover:bg-emerald-dark gap-1"><Save size={14} /> {saving ? "Saving…" : "Save & Publish"}</Button>
        </div>
      </div>
      <p className="text-sm text-slate-500">Drag <GripVertical size={13} className="inline" /> to reorder. Toggle a field off to hide it without deleting. "Required" and max length control validation.</p>

      <div className="space-y-3">
        {list.map((f, i) => (
          <div key={f.id} draggable onDragStart={() => (dragIdx.current = i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
            data-testid={`fb-field-${i}`} className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-start gap-3">
              <span className="cursor-grab text-slate-400 mt-2" data-testid={`fb-drag-${i}`}><GripVertical size={18} /></span>
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <div><Label className="text-xs">Label</Label><Input className="mt-1 h-9" value={f.label} onChange={(e) => upd(i, "label", e.target.value)} data-testid={`fb-label-${i}`} /></div>
                <div><Label className="text-xs">Field name (key)</Label><Input className="mt-1 h-9" value={f.name} onChange={(e) => upd(i, "name", e.target.value.replace(/\s+/g, "_").toLowerCase())} data-testid={`fb-name-${i}`} /></div>
                <div><Label className="text-xs">Type</Label>
                  <select className="mt-1 h-9 w-full rounded-md border border-slate-200 text-sm px-2 bg-white" value={f.type} onChange={(e) => upd(i, "type", e.target.value)} data-testid={`fb-type-${i}`}>
                    {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div><Label className="text-xs">Placeholder</Label><Input className="mt-1 h-9" value={f.placeholder || ""} onChange={(e) => upd(i, "placeholder", e.target.value)} /></div>
                <div><Label className="text-xs">Max length (0 = none)</Label><Input type="number" className="mt-1 h-9" value={f.maxlength || 0} onChange={(e) => upd(i, "maxlength", parseInt(e.target.value || "0", 10))} data-testid={`fb-maxlen-${i}`} /></div>
                <div><Label className="text-xs">Width</Label>
                  <select className="mt-1 h-9 w-full rounded-md border border-slate-200 text-sm px-2 bg-white" value={f.width} onChange={(e) => upd(i, "width", e.target.value)}>
                    <option value="half">Half</option><option value="full">Full</option>
                  </select>
                </div>
                {f.type === "select" && <div className="sm:col-span-2 lg:col-span-3"><Label className="text-xs">Options (comma separated)</Label><Input className="mt-1 h-9" value={(f.options || []).join(", ")} onChange={(e) => upd(i, "options", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} /></div>}
                <div className="flex items-center gap-4 sm:col-span-2 lg:col-span-3">
                  <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" className="h-4 w-4 accent-emerald-600" checked={f.required} onChange={(e) => upd(i, "required", e.target.checked)} data-testid={`fb-required-${i}`} /> Required</label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" className="h-4 w-4 accent-emerald-600" checked={f.enabled !== false} onChange={(e) => upd(i, "enabled", e.target.checked)} data-testid={`fb-enabled-${i}`} /> Show on form</label>
                </div>
              </div>
              <button onClick={() => del(i)} className="text-slate-400 hover:text-red-500 mt-2" data-testid={`fb-delete-${i}`}><Trash2 size={17} /></button>
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-400">No fields yet. Click "Add Field".</div>}
      </div>
    </div>
  );
}


function SeoAnalytics({ content, setContent }) {
  const [c, setC] = useState(content);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [range, setRange] = useState(0);
  const seo = c.seo || {};
  const setSeo = (k, v) => setC({ ...c, seo: { ...(c.seo || {}), [k]: v } });
  const setPage = (path, k, v) => setC({ ...c, seo: { ...(c.seo || {}), pages: { ...((c.seo || {}).pages || {}), [path]: { ...(((c.seo || {}).pages || {})[path] || {}), [k]: v } } } });
  const token = localStorage.getItem("iws_token");
  useEffect(() => { api.get(`/admin/analytics?days=${range}`).then((r) => setStats(r.data)).catch(() => {}); }, [range]);
  const save = async () => {
    setSaving(true);
    try { await api.put("/content", c); setContent(c); toast.success("SEO settings saved & published"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };
  const PAGES = [["/jobs", "Jobs"], ["/hire-talent", "Hire Talent"], ["/submit-resume", "Submit Resume"], ["/contact", "Contact"]];

  return (
    <div className="space-y-8 max-w-4xl" data-testid="seo-panel">
      <Card title="SEO & AI-Search (GEO/AEO)">
        <p className="text-sm text-slate-500 -mt-2">These power your page title, search snippets, social previews (Open Graph) and the JSON-LD schema that helps Google &amp; AI search engines understand your site. A sitemap.xml and robots.txt are also served automatically.</p>
        <div><Label>Meta title</Label><Input data-testid="seo-title" className="mt-1.5" value={seo.title || ""} onChange={(e) => setSeo("title", e.target.value)} /></div>
        <div><Label>Meta description</Label><Textarea data-testid="seo-description" className="mt-1.5" rows={2} value={seo.description || ""} onChange={(e) => setSeo("description", e.target.value)} /></div>
        <div><Label>Keywords (comma separated)</Label><Input data-testid="seo-keywords" className="mt-1.5" value={seo.keywords || ""} onChange={(e) => setSeo("keywords", e.target.value)} /></div>
        <MediaUpload label="Social share image" value={seo.og_image} onChange={(id) => setSeo("og_image", id)} hint="Open Graph image shown when your site is shared (recommended 1200×630)." />
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Organization name</Label><Input className="mt-1.5" value={seo.org_name || ""} onChange={(e) => setSeo("org_name", e.target.value)} /></div>
          <div><Label>Website URL</Label><Input className="mt-1.5" placeholder="https://ishaanworkforcesolutions.com" value={seo.org_url || ""} onChange={(e) => setSeo("org_url", e.target.value)} /></div>
        </div>
      </Card>
      <Card title="Per-Page SEO">
        <p className="text-sm text-slate-500 -mt-2">Optional custom title &amp; description for individual pages (leave blank to use the defaults above).</p>
        {PAGES.map(([path, label]) => { const pg = (seo.pages || {})[path] || {}; return (
          <div key={path} className="grid sm:grid-cols-2 gap-3 border-b border-slate-100 pb-3 last:border-0">
            <div><Label className="text-xs">{label} — title</Label><Input data-testid={`seo-page-title-${label.toLowerCase().replace(/\s/g,'-')}`} className="mt-1 h-9" value={pg.title || ""} onChange={(e) => setPage(path, "title", e.target.value)} /></div>
            <div><Label className="text-xs">{label} — description</Label><Input className="mt-1 h-9" value={pg.description || ""} onChange={(e) => setPage(path, "description", e.target.value)} /></div>
          </div>
        ); })}
      </Card>
      <Card title="Google Analytics 4">
        <p className="text-sm text-slate-500 -mt-2">Paste your GA4 Measurement ID (looks like <b>G-XXXXXXX</b>) to enable Google Analytics. Leave blank to use only the built-in analytics below.</p>
        <div className="max-w-xs"><Label>GA4 Measurement ID</Label><Input data-testid="seo-ga4" className="mt-1.5" placeholder="G-XXXXXXXXXX" value={seo.ga4_id || ""} onChange={(e) => setSeo("ga4_id", e.target.value)} /></div>
      </Card>
      <div className="flex justify-end"><Button onClick={save} disabled={saving} data-testid="seo-save" className="bg-emerald-brand hover:bg-emerald-dark gap-2"><Save size={16} /> {saving ? "Saving…" : "Save & Publish"}</Button></div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <h3 className="font-display font-semibold text-navy text-lg">Built-in Visitor Analytics</h3>
          <div className="flex items-center gap-2">
            {[[0, "All"], [7, "7d"], [30, "30d"], [90, "90d"]].map(([d, l]) => (
              <button key={l} onClick={() => setRange(d)} data-testid={`range-${l}`} className={`px-3 py-1 rounded-full text-xs font-medium ${range === d ? "bg-navy text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
            ))}
            <Button size="sm" variant="outline" onClick={() => window.open(`${API}/admin/analytics/export?auth=${token}&days=${range}`, "_blank")} data-testid="analytics-export" className="gap-1 border-navy text-navy hover:bg-navy hover:text-white"><Download size={14} /> CSV</Button>
          </div>
        </div>
        <p className="text-sm text-slate-500 mb-5">Private, first-party page-view tracking — no third-party account needed.</p>
        {!stats ? <p className="text-slate-500">Loading analytics…</p> : (
          <div className="space-y-6" data-testid="analytics-block">
            <div className="grid grid-cols-3 gap-4">
              <Kpi label="Total Page Views" value={stats.total} icon={Eye} accent="bg-navy/5 text-navy" />
              <Kpi label="Views Today" value={stats.today} icon={TrendingUp} accent="bg-emerald-brand/10 text-emerald-brand" />
              <Kpi label="Pages Tracked" value={stats.unique_pages} icon={FileText} accent="bg-navy/5 text-navy" />
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h4 className="font-display font-semibold text-navy mb-4">Views trend</h4>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                  <BarChart data={stats.daily}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                    <XAxis dataKey="date" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                    <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                    <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                    <Bar dataKey="views" fill="#2E8B57" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {[["Top Pages", stats.top_pages, "path"], ["Top Sources", stats.top_sources, "source"], ["Top Landing Pages", stats.top_landing, "path"], ["Referrer URLs", stats.top_referrers, "referrer"]].map(([title, rows, k]) => (
                <div key={title} className="bg-white rounded-xl border border-slate-200 p-6">
                  <h4 className="font-display font-semibold text-navy mb-3">{title}</h4>
                  {(rows || []).length === 0 ? <p className="text-sm text-slate-400">No data yet.</p> : rows.map((r, i) => (
                    <div key={i} className="flex justify-between gap-3 py-1.5 border-b border-slate-100 last:border-0 text-sm"><span className="text-slate-600 truncate">{r[k]}</span><span className="font-medium text-navy shrink-0">{r.count}</span></div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
